import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Link,
  NavLink,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  BedDouble,
  Building2,
  ChevronRight,
  CircleUserRound,
  Heart,
  Mail,
  MapPin,
  Menu,
  Phone,
  Ruler,
  Search,
  X,
} from "lucide-react";
import {
  assetUrl,
  clearSession,
  getAdviser,
  getAdvisers,
  getFavorite,
  getListing,
  getListings,
  getToken,
  getUser,
  toggleFavorite,
} from "./api";
import { LoginPage, RegisterPage } from "./AuthPage";
import { MessagesPage } from "./MessagesPage";
import { ProfilePage } from "./ProfilePage";
import { ListingFormPage } from "./ListingFormPage";
import { AdminPage } from "./AdminPage";
import { ProfileEditPage } from "./ProfileEditPage";
import { MessagesInboxPage } from "./MessagesInboxPage";
import { ReelsPage } from "./ReelsPage";
import "./styles.css";
import "./filters.css";
import "./profile.css";
import "./forms.css";
import "./admin.css";
import "./instagram.css";
import "./listings.css";

const money = (value) => {
  if (!value) return "Fiyat bilgisi için iletişime geçin";
  if (/tl|₺/i.test(value)) return value;
  const amount = Number(String(value).replaceAll(".", "").replace(",", "."));
  return Number.isFinite(amount)
    ? `${new Intl.NumberFormat("tr-TR").format(amount)} TL`
    : `${value} TL`;
};

function Header() {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(getUser());
  useEffect(() => {
    const refresh = () => setUser(getUser());
    window.addEventListener("authChange", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("authChange", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  const nav = [
    ["/", "Ana Sayfa"],
    ["/ilanlar", "İlanlar"],
    ["/reels", "Reels"],
    ["/danismanlar", "Danışmanlar"],
    ["/hakkimizda", "Hakkımızda"],
    ["/destek", "Destek"],
  ];
  return (
    <header className="site-header">
      <div className="shell header-inner">
        <Link to="/" className="brand" onClick={() => setOpen(false)}>
          <Building2 /> <span>BARAN EMLAK</span>
        </Link>
        <button
          className="menu-button"
          aria-label="Menüyü aç"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
        <nav className={open ? "nav open" : "nav"}>
          {nav.map(([to, label]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={() => setOpen(false)}
            >
              {label}
            </NavLink>
          ))}
          {user ? (
            <>
              <Link
                className="login-link"
                to="/profil"
                onClick={() => setOpen(false)}
              >
                {user.ad_soyad || user.kullanici_adi || "Hesabım"}
              </Link>
              {Number(user.rol_id) === 1 && (
                <Link to="/admin" onClick={() => setOpen(false)}>
                  Yönetim
                </Link>
              )}
              <button
                className="nav-logout"
                onClick={() => {
                  clearSession();
                  setUser(null);
                  setOpen(false);
                  window.dispatchEvent(new Event("authChange"));
                }}
              >
                Çıkış
              </button>
            </>
          ) : (
            <Link
              className="login-link"
              to="/giris"
              onClick={() => setOpen(false)}
            >
              Giriş Yap
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <div className="shell footer">
        <div>
          <strong>BARAN EMLAK</strong>
          <p>Gayrimenkulde güvenilir çözüm ortağınız.</p>
        </div>
        <div>
          <p>
            <Link to="/gizlilik">Gizlilik</Link> ·{" "}
            <Link to="/kullanim-kosullari">Kullanım koşulları</Link>
          </p>
          <p>© {new Date().getFullYear()} Baran Emlak. Tüm hakları saklıdır.</p>
        </div>
      </div>
    </footer>
  );
}

function Loading() {
  return (
    <div className="loading" role="status">
      Yükleniyor…
    </div>
  );
}
function ErrorBox({ children }) {
  return (
    <div className="error" role="alert">
      {children}
    </div>
  );
}
function ImageOrPlaceholder({ src, fallbackSrc, alt, className = "" }) {
  return src ? (
    <img
      className={className}
      src={assetUrl(src)}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={(event) => {
        if (fallbackSrc && event.currentTarget.src !== assetUrl(fallbackSrc))
          event.currentTarget.src = assetUrl(fallbackSrc);
      }}
    />
  ) : (
    <div className={`image-placeholder ${className}`}>
      <Building2 />
      <span>Görsel yakında</span>
    </div>
  );
}
function instagramEmbedUrl(value) {
  try {
    const url = new URL(value);
    if (!/(^|\.)instagram\.com$/i.test(url.hostname)) return "";
    const match = url.pathname.match(/^\/(p|reel|tv)\/([A-Za-z0-9_-]+)/i);
    return match
      ? `https://www.instagram.com/${match[1].toLowerCase()}/${match[2]}/embed/captioned/`
      : "";
  } catch {
    return "";
  }
}
const photoVersion = (photo, version) =>
  photo?.[`${version}_url`] || photo?.dosya_yolu;

const detailLabels = {
  emlak_tipi: "Gayrimenkul tipi",
  brut_metrekare: "Brüt alan",
  net_metrekare: "Net alan",
  oda_sayisi: "Oda sayısı",
  bina_yasi: "Bina yaşı",
  bulundugu_kat: "Bulunduğu kat",
  kat_sayisi: "Kat sayısı",
  isitma: "Isıtma",
  banyo_sayisi: "Banyo sayısı",
  mutfak: "Mutfak",
  balkon: "Balkon",
  asansor: "Asansör",
  otopark: "Otopark",
  esyali: "Eşyalı",
  kullanim_durumu: "Kullanım durumu",
  site_icerisinde: "Site içerisinde",
  site_adi: "Site adı",
  aidat_tl: "Aidat",
  krediye_uygun: "Krediye uygun",
  gayrimenkul_sahibi: "Gayrimenkul sahibi",
  tapu_durumu: "Tapu durumu",
  tasinmaz_numarasi: "Taşınmaz numarası",
  kimden: "Kimden",
  takas: "Takas",
  city: "Şehir",
  district: "İlçe",
  neighborhood: "Mahalle",
  adres: "Adres",
  durum: "İlan durumu",
  goruntulenme_sayisi: "Görüntülenme",
  favori_sayisi: "Favori sayısı",
};
const booleanDetails = new Set([
  "balkon",
  "asansor",
  "otopark",
  "esyali",
  "site_icerisinde",
  "takas",
  "bati",
  "dogu",
  "guney",
  "kuzey",
  "teras",
  "klima",
  "kablo_tv",
  "havuz",
]);
const humanizeKey = (key) =>
  detailLabels[key] ||
  key
    .replace(/^Özellikler\./, "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toLocaleUpperCase("tr-TR"));
const detailValue = (key, value) =>
  booleanDetails.has(key) || typeof value === "boolean"
    ? Number(value)
      ? "Var"
      : "Yok"
    : String(value);
const displayDate = (value) =>
  value
    ? new Intl.DateTimeFormat("tr-TR", {
        dateStyle: "long",
        timeStyle: "short",
      }).format(new Date(String(value).replace(" ", "T")))
    : "—";

function DetailTable({ title, items }) {
  const visible = items.filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
  if (!visible.length) return null;
  return (
    <section className="detail-info">
      <h2>{title}</h2>
      <dl>
        {visible.map(([key, value]) => (
          <div key={key}>
            <dt>{humanizeKey(key)}</dt>
            <dd>{detailValue(key, value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function FavoriteButton({ listingId }) {
  const [favorite, setFavorite] = useState(null);
  const [busy, setBusy] = useState(false);
  const signedIn = Boolean(getToken());
  useEffect(() => {
    let active = true;
    if (!signedIn)
      return () => {
        active = false;
      };
    getFavorite(listingId)
      .then((state) => active && setFavorite(state))
      .catch(() => active && setFavorite(null));
    return () => {
      active = false;
    };
  }, [listingId, signedIn]);
  if (!signedIn)
    return (
      <Link
        className="favorite-button"
        to={`/giris?next=${encodeURIComponent(`/ilan/${listingId}`)}`}
      >
        <Heart /> Favorilere ekle
      </Link>
    );
  const change = async () => {
    if (busy) return;
    setBusy(true);
    try {
      setFavorite(await toggleFavorite(listingId));
    } finally {
      setBusy(false);
    }
  };
  const active = Boolean(favorite?.is_favorite);
  return (
    <button
      type="button"
      className={`favorite-button${active ? " active" : ""}`}
      onClick={change}
      disabled={busy}
      aria-pressed={active}
    >
      <Heart fill={active ? "currentColor" : "none"} />
      {busy ? "Güncelleniyor…" : active ? "Favoriden çıkar" : "Favorilere ekle"}
    </button>
  );
}

function ListingCard({ listing }) {
  const originalPhoto =
    listing.kapak_foto || listing.goruntuler?.[0]?.dosya_yolu;
  const photo =
    listing.kapak_foto_thumbnail ||
    listing.goruntuler?.[0]?.thumbnail_url ||
    originalPhoto;
  return (
    <article className="listing-card">
      <Link to={`/ilan/${listing.ilan_no}`} className="card-image">
        <ImageOrPlaceholder
          src={photo}
          fallbackSrc={originalPhoto}
          alt={listing.baslik}
        />
      </Link>
      <div className="card-body">
        <p className="location">
          <MapPin />{" "}
          {listing.adres ||
            [listing.city, listing.district, listing.neighborhood]
              .filter(Boolean)
              .join(", ") ||
            "Konum bilgisi yok"}
        </p>
        <h3>
          <Link to={`/ilan/${listing.ilan_no}`}>
            {listing.baslik || "Başlıksız ilan"}
          </Link>
        </h3>
        <div className="facts">
          {listing.oda_sayisi && (
            <span>
              <BedDouble /> {listing.oda_sayisi}
            </span>
          )}
          {listing.brut_metrekare && (
            <span>
              <Ruler /> {listing.brut_metrekare} m²
            </span>
          )}
        </div>
        <p className="price">{money(listing.fiyat)}</p>
        <Link className="detail-link" to={`/ilan/${listing.ilan_no}`}>
          İlanı incele <ChevronRight />
        </Link>
      </div>
    </article>
  );
}

const PAGE_SIZE = 12;
const shuffled = (items) => [...items].sort(() => Math.random() - 0.5);
const pageQuery = (query, skip) => {
  const params = new URLSearchParams(query);
  params.set("skip", String(skip));
  params.set("limit", String(PAGE_SIZE));
  return params.toString();
};
function Listings({ limit, title, query = "", shuffleInitial = false }) {
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(!limit);
  const [sentinel, setSentinel] = useState(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setData([]);
    setHasMore(!limit);
    const request = limit
      ? `${query ? `${query}&` : ""}limit=30`
      : pageQuery(query, 0);
    getListings(request)
      .then((items) => {
        if (!active) return;
        const next = limit
          ? shuffled(items).slice(0, limit)
          : shuffleInitial
            ? shuffled(items)
            : items;
        setData(next);
        setHasMore(!limit && items.length === PAGE_SIZE);
      })
      .catch(() => active && setError("İlanlar yüklenirken bir sorun oluştu."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [limit, query, shuffleInitial]);
  useEffect(() => {
    if (limit || !sentinel || loading || loadingMore || !hasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setLoadingMore(true);
        getListings(pageQuery(query, data.length))
          .then((items) => {
            setData((old) => [...old, ...items]);
            setHasMore(items.length === PAGE_SIZE);
          })
          .catch(() => setError("Diğer ilanlar yüklenemedi."))
          .finally(() => setLoadingMore(false));
      },
      { rootMargin: "360px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [data.length, hasMore, limit, loading, loadingMore, query, sentinel]);
  return (
    <section className="section shell">
      {title && (
        <div className="section-heading">
          <div>
            <p className="eyebrow">PORTFÖYÜMÜZ</p>
            <h2>{title}</h2>
          </div>
          {limit && (
            <Link to="/ilanlar">
              Tüm ilanlar <ChevronRight />
            </Link>
          )}
        </div>
      )}
      {loading ? (
        <Loading />
      ) : error && !data.length ? (
        <ErrorBox>{error}</ErrorBox>
      ) : data.length ? (
        <>
          <div className="listing-grid">
            {data.map((listing) => (
              <ListingCard key={listing.ilan_no} listing={listing} />
            ))}
          </div>
          {!limit && (
            <div ref={setSentinel} className="listing-sentinel">
              {loadingMore
                ? "Yeni ilanlar yükleniyor…"
                : hasMore
                  ? "Daha fazla ilan yükleniyor…"
                  : "Tüm ilanlar gösterildi."}
            </div>
          )}
          {error && <ErrorBox>{error}</ErrorBox>}
        </>
      ) : (
        <p className="empty">Aramanıza uygun ilan bulunamadı.</p>
      )}
    </section>
  );
}

function Home() {
  const [term, setTerm] = useState("");
  return (
    <>
      <section className="hero">
        <div className="shell hero-content">
          <p className="eyebrow">BARAN EMLAK</p>
          <h1>Hayalinizdeki evi birlikte bulalım.</h1>
          <p>Satılık ve kiralık seçkin gayrimenkul portföylerini keşfedin.</p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              window.location.href = `${import.meta.env.BASE_URL}ilanlar?search=${encodeURIComponent(term)}`;
            }}
          >
            <Search />
            <input
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="İlan, mahalle veya şehir ara"
              aria-label="İlan ara"
            />
            <button>Ara</button>
          </form>
        </div>
      </section>
      <Listings title="Öne çıkan ilanlar" shuffleInitial />
    </>
  );
}

function ListingsPage() {
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("search") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [category, setCategory] = useState(params.get("ilan_turu") || "");
  const query = useMemo(() => params.toString(), [params]);
  const submit = (event) => {
    event.preventDefault();
    const next = {};
    if (term) next.search = term;
    if (city) next.city = city;
    if (category) next.ilan_turu = category;
    setParams(next);
  };
  return (
    <>
      <section className="page-title">
        <div className="shell">
          <p className="eyebrow">GAYRİMENKUL İLANLARI</p>
          <h1>İlanlar</h1>
          <form className="inline-search filters" onSubmit={submit}>
            <Search />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="İlanlarda ara"
            />
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Şehir"
              aria-label="Şehir"
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="İlan türü"
            >
              <option value="">Tüm ilan türleri</option>
              <option value="Satılık">Satılık</option>
              <option value="Kiralık">Kiralık</option>
            </select>
            <button>Ara</button>
          </form>
        </div>
      </section>
      <Listings query={query} />
    </>
  );
}

function ListingDetail() {
  const { id } = useParams();
  const [item, setItem] = useState();
  const [error, setError] = useState("");
  const [videoOpen, setVideoOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const photoCount = item?.goruntuler?.length || 0;
  useEffect(() => {
    getListing(id)
      .then(setItem)
      .catch(() => setError("İlan bulunamadı veya şu an erişilemiyor."));
  }, [id]);
  useEffect(() => {
    const close = (event) => {
      if (event.key === "Escape") {
        setVideoOpen(false);
        setPhotoOpen(false);
      }
      if (!photoOpen || photoCount < 2) return;
      if (event.key === "ArrowLeft")
        setSelectedPhoto((index) => (index - 1 + photoCount) % photoCount);
      if (event.key === "ArrowRight")
        setSelectedPhoto((index) => (index + 1) % photoCount);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [photoCount, photoOpen]);
  if (error)
    return (
      <main className="shell section">
        <ErrorBox>{error}</ErrorBox>
      </main>
    );
  if (!item) return <Loading />;
  const photos = item.goruntuler || [];
  const visiblePhoto = photos[selectedPhoto] || photos[0];
  const adviser = item.danisman || {};
  const phone = adviser.cep_telefonu || adviser.telefon;
  const name =
    adviser.ad_soyad || adviser.kullanici_adi || "Baran Emlak danışmanı";
  const latitude = item.latitude || item.konum_lat;
  const longitude = item.longitude || item.konum_lng;
  const mapUrl =
    latitude && longitude
      ? `https://www.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}`
      : "";
  const instagramEmbed = instagramEmbedUrl(item.instagram_url);
  const baseDetails = [
    ["emlak_tipi", item.ozellikler?.emlak_tipi],
    ["city", item.city],
    ["district", item.district],
    ["neighborhood", item.neighborhood],
    ["adres", item.adres],
    ["durum", item.durum],
    ["goruntulenme_sayisi", item.goruntulenme_sayisi],
    ["favori_sayisi", item.favori_sayisi],
  ];
  const propertyDetails = Object.entries(item.ozellikler || {}).filter(
    ([key]) => !["id", "created_at", "updated_at", "emlak_tipi"].includes(key),
  );
  const importedDetails = Object.entries(item.listing_features || {}).filter(
    ([, value]) => value !== null && value !== "",
  );
  const localVideos = (item.videolar || []).filter(
    (video) => video.dosya_yolu || video.url,
  );
  const openPhoto = (index) => {
    setSelectedPhoto(index);
    setPhotoOpen(true);
  };
  const previousPhoto = () =>
    setSelectedPhoto((index) => (index - 1 + photos.length) % photos.length);
  const nextPhoto = () =>
    setSelectedPhoto((index) => (index + 1) % photos.length);
  return (
    <main className="shell section detail">
      <Link className="back" to="/ilanlar">
        <ArrowLeft /> İlanlara dön
      </Link>
      <div className="detail-layout">
        <div>
          <button
            type="button"
            className="gallery gallery-button"
            onClick={() => visiblePhoto && setPhotoOpen(true)}
            aria-label="Fotoğrafı tam ekranda aç"
          >
            <ImageOrPlaceholder
              src={photoVersion(visiblePhoto, "medium")}
              fallbackSrc={visiblePhoto?.dosya_yolu}
              alt={item.baslik}
            />
            {visiblePhoto && (
              <span className="gallery-expand">Tam ekranda görüntüle</span>
            )}
          </button>
          {photos.length > 1 && (
            <div className="thumbnail-row">
              {photos.map((photo, index) => (
                <button
                  type="button"
                  className={index === selectedPhoto ? "active" : ""}
                  key={photo.id}
                  onClick={() => setSelectedPhoto(index)}
                  aria-label={`Fotoğraf ${index + 1}`}
                >
                  <ImageOrPlaceholder
                    src={photoVersion(photo, "thumbnail")}
                    fallbackSrc={photo.dosya_yolu}
                    alt={`İlan görseli ${index + 1}`}
                  />
                </button>
              ))}
            </div>
          )}
          {photoOpen && visiblePhoto && (
            <div
              className="photo-modal"
              role="dialog"
              aria-modal="true"
              aria-label="İlan fotoğrafı"
            >
              <button
                type="button"
                className="photo-modal-close"
                onClick={() => setPhotoOpen(false)}
                aria-label="Fotoğrafı kapat"
              >
                ×
              </button>
              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    className="photo-modal-nav previous"
                    onClick={previousPhoto}
                    aria-label="Önceki fotoğraf"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="photo-modal-nav next"
                    onClick={nextPhoto}
                    aria-label="Sonraki fotoğraf"
                  >
                    ›
                  </button>
                </>
              )}
              <img
                src={assetUrl(photoVersion(visiblePhoto, "original"))}
                onError={(event) => {
                  event.currentTarget.src = assetUrl(visiblePhoto.dosya_yolu);
                }}
                alt={item.baslik}
              />
              <span>
                {selectedPhoto + 1} / {photos.length}
              </span>
              <div className="photo-modal-thumbs">
                {photos.map((photo, index) => (
                  <button
                    type="button"
                    className={index === selectedPhoto ? "active" : ""}
                    key={photo.id}
                    onClick={() => openPhoto(index)}
                  >
                    <img
                      src={assetUrl(photoVersion(photo, "thumbnail"))}
                      onError={(event) => {
                        event.currentTarget.src = assetUrl(photo.dosya_yolu);
                      }}
                      alt={`Fotoğraf ${index + 1}`}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
          <h1>{item.baslik}</h1>
          <p className="location">
            <MapPin /> {item.adres || "Konum bilgisi yok"}
          </p>
          {mapUrl && (
            <p>
              <a
                className="map-link"
                href={mapUrl}
                target="_blank"
                rel="noreferrer"
              >
                Haritada görüntüle
              </a>
            </p>
          )}
          <div className="feature-box">
            <span>
              {item.ozellikler?.oda_sayisi ||
                item.listing_features?.["Özellikler.oda_sayisi"] ||
                "—"}
              <small>Oda sayısı</small>
            </span>
            <span>
              {item.ozellikler?.brut_metrekare ||
              item.listing_features?.["Özellikler.m²_brut"]
                ? `${item.ozellikler?.brut_metrekare || item.listing_features?.["Özellikler.m²_brut"]} m²`
                : "—"}
              <small>Brüt alan</small>
            </span>
          </div>
          {localVideos.length > 0 && (
            <section className="listing-video-files">
              <h2>İlan videoları</h2>
              {localVideos.map((video) => (
                <video
                  key={video.id || video.dosya_yolu}
                  controls
                  src={assetUrl(video.dosya_yolu || video.url)}
                >
                  Tarayıcınız videoyu desteklemiyor.
                </video>
              ))}
            </section>
          )}
          {instagramEmbed && (
            <section className="instagram-video">
              <div className="instagram-video-head">
                <div>
                  <p className="eyebrow">INSTAGRAM</p>
                  <h2>İlan videosu</h2>
                </div>
                <button
                  type="button"
                  className="instagram-watch"
                  onClick={() => setVideoOpen(true)}
                >
                  Instagram’da izle
                </button>
              </div>
              <div className="instagram-frame">
                <iframe
                  src={instagramEmbed}
                  title="İlan Instagram videosu"
                  loading="lazy"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; fullscreen"
                  allowFullScreen
                />
              </div>
              <button
                type="button"
                className="instagram-fullscreen"
                onClick={() => setVideoOpen(true)}
              >
                Videoyu tam ekranda izle
              </button>
              {videoOpen && (
                <div
                  className="instagram-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="İlan Instagram videosu"
                >
                  <button
                    type="button"
                    className="instagram-modal-close"
                    onClick={() => setVideoOpen(false)}
                    aria-label="Videoyu kapat"
                  >
                    ×
                  </button>
                  <iframe
                    src={instagramEmbed}
                    title="İlan Instagram videosu tam ekran"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; fullscreen"
                    allowFullScreen
                  />
                </div>
              )}
            </section>
          )}
          <h2>Açıklama</h2>
          <p className="description">
            {String(
              item.aciklama || "Bu ilan için henüz açıklama eklenmemiştir.",
            ).replace(/<br\s*\/?\s*>/gi, "\n")}
          </p>
          <DetailTable title="İlan bilgileri" items={baseDetails} />
          <DetailTable
            title="Gayrimenkul özellikleri"
            items={propertyDetails}
          />
          <DetailTable title="Ek özellikler" items={importedDetails} />
          <section className="detail-meta">
            <span>İlan no: {item.ilan_no}</span>
            <span>Eklenme: {displayDate(item.created_at)}</span>
            <span>Son güncelleme: {displayDate(item.updated_at)}</span>
          </section>
        </div>
        <aside className="contact-card">
          <p className="eyebrow">SATIŞ FİYATI</p>
          <strong>{money(item.fiyat)}</strong>
          <FavoriteButton listingId={item.ilan_no} />
          <hr />
          <h2>{name}</h2>
          <p>
            Bu ilan hakkında doğrudan danışmanımızla iletişime geçebilirsiniz.
          </p>
          {phone && (
            <a href={`tel:${phone}`}>
              <Phone /> Hemen ara
            </a>
          )}
          {adviser.email && (
            <a className="outline" href={`mailto:${adviser.email}`}>
              <Mail /> E-posta gönder
            </a>
          )}
          {adviser.id && (
            <Link className="outline" to={`/mesaj/${item.ilan_no}`}>
              Mesaj gönder
            </Link>
          )}
        </aside>
      </div>
    </main>
  );
}

function Advisers() {
  const [items, setItems] = useState();
  const [error, setError] = useState("");
  useEffect(() => {
    getAdvisers()
      .then(setItems)
      .catch(() => setError("Danışmanlar yüklenemedi."));
  }, []);
  return (
    <main>
      <section className="page-title">
        <div className="shell">
          <p className="eyebrow">EKİBİMİZ</p>
          <h1>Danışmanlarımız</h1>
          <p>Gayrimenkul yolculuğunuzda yanınızdayız.</p>
        </div>
      </section>
      <section className="shell section">
        {!items ? (
          <Loading />
        ) : error ? (
          <ErrorBox>{error}</ErrorBox>
        ) : (
          <div className="adviser-grid">
            {items.map((adviser) => (
              <article className="adviser" key={adviser.id}>
                <ImageOrPlaceholder
                  src={adviser.profil_resmi}
                  alt={adviser.ad_soyad || adviser.kullanici_adi}
                />
                <h2>{adviser.ad_soyad || adviser.kullanici_adi}</h2>
                <p>{adviser.ilan_sayisi} aktif ilan</p>
                <Link
                  className="advisor-listings-link"
                  to={`/ilanlar?advisor_id=${encodeURIComponent(adviser.id)}`}
                >
                  İlanlarını görüntüle <ChevronRight />
                </Link>
                {adviser.cep_telefonu || adviser.telefon ? (
                  <a href={`tel:${adviser.cep_telefonu || adviser.telefon}`}>
                    <Phone /> İletişime geç
                  </a>
                ) : (
                  <span className="muted">İletişim bilgisi yakında</span>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function InfoPage({ title, children }) {
  return (
    <main>
      <section className="page-title">
        <div className="shell">
          <p className="eyebrow">BARAN EMLAK</p>
          <h1>{title}</h1>
        </div>
      </section>
      <section className="shell section prose">{children}</section>
    </main>
  );
}
function App() {
  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/ilanlar" element={<ListingsPage />} />
        <Route path="/reels" element={<ReelsPage />} />
        <Route path="/ilan/:id" element={<ListingDetail />} />
        <Route path="/danismanlar" element={<Advisers />} />
        <Route path="/giris" element={<LoginPage />} />
        <Route path="/kayit" element={<RegisterPage />} />
        <Route path="/profil" element={<ProfilePage />} />
        <Route path="/profil-duzenle" element={<ProfileEditPage />} />
        <Route path="/mesajlar" element={<MessagesInboxPage />} />
        <Route path="/ilan-ver" element={<ListingFormPage />} />
        <Route path="/ilan-duzenle/:listingId" element={<ListingFormPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/mesaj/:listingId" element={<MessagesPage />} />
        <Route
          path="/hakkimizda"
          element={
            <InfoPage title="Hakkımızda">
              <p>
                Baran Emlak; satılık ve kiralık gayrimenkul arayanları, yerel
                piyasa bilgisine sahip danışmanlarla buluşturur. İlanlar, onay
                süreci sonrasında yayınlanır.
              </p>
              <p>
                Portföy, fiyat ve ziyaret talepleri için danışmanlarımızla
                doğrudan iletişime geçebilirsiniz.
              </p>
            </InfoPage>
          }
        />
        <Route
          path="/destek"
          element={
            <InfoPage title="Destek">
              <p>
                İlanlar, üyelik ve hizmetlerimiz hakkında bilgi almak için
                bizimle iletişime geçin.
              </p>
              <p>
                <a href="tel:+905337245004">+90 533 724 50 04</a> ·{" "}
                <a href="mailto:baranemlak@gmail.com">baranemlak@gmail.com</a>
              </p>
            </InfoPage>
          }
        />
        <Route
          path="/gizlilik"
          element={
            <InfoPage title="Gizlilik">
              <p>
                Üyelik ve iletişim sırasında paylaştığınız bilgiler yalnızca
                hizmetin sunulması, güvenlik ve yasal yükümlülükler için
                işlenir. Bilgileriniz üçüncü kişilere pazarlama amacıyla
                satılmaz.
              </p>
              <p>
                Hesap veya veri talepleriniz için destek kanallarımızdan bizimle
                iletişime geçebilirsiniz.
              </p>
            </InfoPage>
          }
        />
        <Route
          path="/kullanim-kosullari"
          element={
            <InfoPage title="Kullanım koşulları">
              <p>
                İlan verenler, içeriklerinin doğru, güncel ve hukuka uygun
                olmasından sorumludur. Şikâyet edilen veya kurallara aykırı
                içerikler inceleme sonrasında yayından kaldırılabilir.
              </p>
              <p>
                Platform güvenliğini korumak için hesaplar ve ilanlar
                gerektiğinde geçici olarak kısıtlanabilir.
              </p>
            </InfoPage>
          }
        />
        <Route
          path="*"
          element={
            <InfoPage title="Sayfa bulunamadı">
              <Link className="button" to="/">
                Ana sayfaya dön
              </Link>
            </InfoPage>
          }
        />
      </Routes>
      <Footer />
    </>
  );
}

createRoot(document.getElementById("root")).render(
  <BrowserRouter basename={import.meta.env.BASE_URL}>
    <App />
  </BrowserRouter>,
);
